// TermsPage.jsx
import React, { useEffect } from 'react';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Link } from 'react-router-dom';
import './TermsPage.scss';

const TermsPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    // Table of contents
    const sections = [
        { id: 'introduction', title: '1. Introduction' },
        { id: 'definitions', title: '2. Definitions' },
        { id: 'services', title: '3. Our Services' },
        { id: 'client-obligations', title: '4. Client Obligations' },
        { id: 'fees-payment', title: '5. Fees & Payment' },
        { id: 'intellectual-property', title: '6. Intellectual Property' },
        { id: 'confidentiality', title: '7. Confidentiality' },
        { id: 'liability', title: '8. Limitation of Liability' },
        { id: 'warranties', title: '9. Warranties & Disclaimers' },
        { id: 'termination', title: '10. Termination' },
        { id: 'data-protection', title: '11. Data Protection & GDPR' },
        { id: 'force-majeure', title: '12. Force Majeure' },
        { id: 'governing-law', title: '13. Governing Law & Jurisdiction' },
        { id: 'general', title: '14. General Provisions' },
        { id: 'contact', title: '15. Contact Information' }
    ];

    return (
        <div className="terms-page">
            {/* Hero Section */}
            <section className="terms-hero-section py-5 pt-7 bg-light">
                <Container>
                    <Row className="text-center">
                        <Col lg={8} className="mx-auto">
                            <div className="terms-badge mb-3">
                                <span className="badge">Legal Agreement</span>
                            </div>
                            <h1 className="terms-hero-title display-4 fw-bold mb-4">
                                Terms and Conditions
                                <span className="terms-gradient-text d-block">of Service</span>
                            </h1>
                            <p className="terms-hero-subtitle lead text-muted mb-4">
                                These Terms and Conditions govern your use of Reachtix's digital marketing
                                services, website, and platform. Please read them carefully before engaging
                                our services.
                            </p>
                            <div className="terms-meta-info">
                                <p><strong>Last Updated:</strong> 1st April 2026</p>
                                <p><strong>Version:</strong> 2.0</p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Main Content */}
            <section className="terms-main-section py-5">
                <Container>
                    <Row>
                        {/* Sidebar Navigation */}
                        <Col lg={3} className="mb-5 mb-lg-0">
                            <Card className="terms-sidebar-card sticky-top" style={{ top: '100px' }}>
                                <CardBody className="p-4">
                                    <h6 className="terms-sidebar-title fw-bold mb-3">Table of Contents</h6>
                                    <nav className="terms-sidebar-nav">
                                        <ul className="list-unstyled mb-0">
                                            {sections.map((section) => (
                                                <li key={section.id} className="mb-2">
                                                    <a
                                                        href={`#${section.id}`}
                                                        className="terms-sidebar-link text-decoration-none d-block py-2 border-bottom border-light"
                                                    >
                                                        {section.title}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </nav>
                                    <div className="terms-sidebar-footer mt-4 pt-3 border-top">
                                        <small className="text-muted">
                                            Need help? <Link to="/contact" className="terms-contact-link">Contact us</Link>
                                        </small>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Main Content */}
                        <Col lg={9}>
                            <div className="terms-content-wrapper">
                                {/* Introduction */}
                                <section id="introduction" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">1. Introduction</h2>
                                    <p className="terms-section-text">
                                        Welcome to Reachtix ("we," "our," "us"). These Terms and Conditions
                                        ("Terms") govern your access to and use of our website, platform, and
                                        digital marketing services (collectively, the "Services").
                                    </p>
                                    <p className="terms-section-text">
                                        By accessing or using our Services, you agree to be bound by these Terms.
                                        If you are entering into these Terms on behalf of a company or other
                                        legal entity, you represent that you have the authority to bind such
                                        entity to these Terms.
                                    </p>
                                    <div className="terms-alert-info mt-4">
                                        <strong>Important Notice:</strong> These Terms constitute a legally
                                        binding agreement between you ("Client," "you," "your") and
                                        QUANTUMCREST SOLUTIONS LTD (trading as Reachtix). Please read them
                                        carefully before engaging our Services.
                                    </div>
                                </section>

                                {/* Definitions */}
                                <section id="definitions" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">2. Definitions</h2>
                                    <p className="terms-section-text">In these Terms, the following definitions apply:</p>

                                    <div className="terms-definition-list mt-4">
                                        <div className="terms-definition-item mb-3 pb-2 border-bottom">
                                            <h6 className="terms-definition-term fw-bold mb-1">"Agreement"</h6>
                                            <p className="terms-definition-text mb-0 small text-muted">Collectively refers to these Terms, any Statement of Work, and any other documents incorporated by reference.</p>
                                        </div>
                                        <div className="terms-definition-item mb-3 pb-2 border-bottom">
                                            <h6 className="terms-definition-term fw-bold mb-1">"Client" or "You"</h6>
                                            <p className="terms-definition-text mb-0 small text-muted">The individual or entity engaging our Services.</p>
                                        </div>
                                        <div className="terms-definition-item mb-3 pb-2 border-bottom">
                                            <h6 className="terms-definition-term fw-bold mb-1">"Deliverables"</h6>
                                            <p className="terms-definition-text mb-0 small text-muted">All materials, reports, campaigns, strategies, and other work product created by us for you as part of the Services.</p>
                                        </div>
                                        <div className="terms-definition-item mb-3 pb-2 border-bottom">
                                            <h6 className="terms-definition-term fw-bold mb-1">"Intellectual Property Rights"</h6>
                                            <p className="terms-definition-text mb-0 small text-muted">Patents, copyrights, trademarks, trade secrets, and all other proprietary rights.</p>
                                        </div>
                                        <div className="terms-definition-item mb-3 pb-2 border-bottom">
                                            <h6 className="terms-definition-term fw-bold mb-1">"Services"</h6>
                                            <p className="terms-definition-text mb-0 small text-muted">The digital marketing services provided by Reachtix, including SEO, Paid Media, Data & AI, Strategy, and Experience services.</p>
                                        </div>
                                        <div className="terms-definition-item mb-3 pb-2 border-bottom">
                                            <h6 className="terms-definition-term fw-bold mb-1">"Statement of Work" or "SOW"</h6>
                                            <p className="terms-definition-text mb-0 small text-muted">A written document detailing the specific Services to be provided, fees, timeline, and deliverables.</p>
                                        </div>
                                        <div className="terms-definition-item mb-3 pb-2">
                                            <h6 className="terms-definition-term fw-bold mb-1">"Third-Party Platforms"</h6>
                                            <p className="terms-definition-text mb-0 small text-muted">External platforms such as Google Ads, Meta Ads, TikTok, LinkedIn, and other advertising or analytics platforms.</p>
                                        </div>
                                    </div>
                                </section>

                                {/* Our Services */}
                                <section id="services" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">3. Our Services</h2>
                                    <p className="terms-section-text">
                                        Reachtix provides a comprehensive suite of digital marketing services,
                                        including but not limited to:
                                    </p>

                                    <Row className="mt-4">
                                        <Col md={6} className="mb-3">
                                            <div className="terms-service-card p-3 border rounded h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <i className="fas fa-search text-primary me-2"></i>
                                                    <h6 className="fw-bold mb-0">SEO Services</h6>
                                                </div>
                                                <p className="small text-muted mb-0">AI SEO, GEO, Technical SEO, Ecommerce SEO, International SEO</p>
                                            </div>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <div className="terms-service-card p-3 border rounded h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <i className="fas fa-ad text-primary me-2"></i>
                                                    <h6 className="fw-bold mb-0">Paid Media</h6>
                                                </div>
                                                <p className="small text-muted mb-0">PPC, Paid Social, Programmatic, Meta Advertising, PPC Consultancy</p>
                                            </div>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <div className="terms-service-card p-3 border rounded h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <i className="fas fa-database text-primary me-2"></i>
                                                    <h6 className="fw-bold mb-0">Data & AI</h6>
                                                </div>
                                                <p className="small text-muted mb-0">AI & Automation, Analytics, Tag Management</p>
                                            </div>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <div className="terms-service-card p-3 border rounded h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <i className="fas fa-chess text-primary me-2"></i>
                                                    <h6 className="fw-bold mb-0">Strategy</h6>
                                                </div>
                                                <p className="small text-muted mb-0">Audits & Consultancy, Market Research, Media Planning, In-housing, International Marketing</p>
                                            </div>
                                        </Col>
                                        <Col md={12} className="mb-3">
                                            <div className="terms-service-card p-3 border rounded h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <i className="fas fa-palette text-primary me-2"></i>
                                                    <h6 className="fw-bold mb-0">Experience</h6>
                                                </div>
                                                <p className="small text-muted mb-0">Creative, CRO, Technology, Video</p>
                                            </div>
                                        </Col>
                                    </Row>

                                    <h5 className="terms-subsection-title mt-4 mb-3">3.1 Statements of Work</h5>
                                    <p className="terms-section-text">
                                        Specific Services will be detailed in one or more Statements of Work (SOW)
                                        executed by both parties. Each SOW will include:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>Description of Services and Deliverables</li>
                                        <li>Project timeline and milestones</li>
                                        <li>Fees and payment schedule</li>
                                        <li>Key performance indicators (KPIs) where applicable</li>
                                        <li>Any specific assumptions or dependencies</li>
                                    </ul>
                                    <p className="terms-section-text">
                                        In the event of any conflict between these Terms and a SOW, the SOW shall
                                        control with respect to the specific Services covered.
                                    </p>

                                    <h5 className="terms-subsection-title mt-4 mb-3">3.2 Third-Party Platforms</h5>
                                    <p className="terms-section-text">
                                        Our Services may involve the use of Third-Party Platforms. You acknowledge that:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>Your use of Third-Party Platforms is subject to their respective terms of service</li>
                                        <li>We are not responsible for the actions, errors, or outages of Third-Party Platforms</li>
                                        <li>You grant us necessary access to your accounts on Third-Party Platforms to perform the Services</li>
                                        <li>You are responsible for maintaining the security of your account credentials</li>
                                    </ul>

                                    <div className="terms-alert-warning mt-4">
                                        <strong>Platform Specific Terms:</strong> When engaging our Paid Media
                                        services, you acknowledge that advertising platforms (Google, Meta, etc.)
                                        have their own policies. We will comply with these policies but cannot
                                        guarantee ad approval or account standing.
                                    </div>
                                </section>

                                {/* Client Obligations */}
                                <section id="client-obligations" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">4. Client Obligations</h2>
                                    <p className="terms-section-text">
                                        To enable us to provide the Services effectively, you agree to:
                                    </p>

                                    <div className="terms-obligations-grid mt-4">
                                        <Row>
                                            <Col md={6} className="mb-3">
                                                <div className="terms-obligation-card p-3 border rounded">
                                                    <i className="fas fa-clock fa-lg text-primary mb-2"></i>
                                                    <h6 className="fw-bold mb-1">Timely Cooperation</h6>
                                                    <p className="small text-muted mb-0">Provide timely responses, feedback, and approvals as reasonably required</p>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="terms-obligation-card p-3 border rounded">
                                                    <i className="fas fa-key fa-lg text-primary mb-2"></i>
                                                    <h6 className="fw-bold mb-1">Account Access</h6>
                                                    <p className="small text-muted mb-0">Grant necessary access to your digital assets, analytics, and advertising accounts</p>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="terms-obligation-card p-3 border rounded">
                                                    <i className="fas fa-gavel fa-lg text-primary mb-2"></i>
                                                    <h6 className="fw-bold mb-1">Compliance</h6>
                                                    <p className="small text-muted mb-0">Ensure all materials you provide comply with applicable laws and regulations</p>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="terms-obligation-card p-3 border rounded">
                                                    <i className="fas fa-chart-line fa-lg text-primary mb-2"></i>
                                                    <h6 className="fw-bold mb-1">Performance Data</h6>
                                                    <p className="small text-muted mb-0">Provide accurate information about your business, products, and target audience</p>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="terms-obligation-card p-3 border rounded">
                                                    <i className="fas fa-euro-sign fa-lg text-primary mb-2"></i>
                                                    <h6 className="fw-bold mb-1">Advertising Budget</h6>
                                                    <p className="small text-muted mb-0">Ensure sufficient funds are available for advertising spend on Third-Party Platforms</p>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="terms-obligation-card p-3 border rounded">
                                                    <i className="fas fa-ban fa-lg text-primary mb-2"></i>
                                                    <h6 className="fw-bold mb-1">Prohibited Content</h6>
                                                    <p className="small text-muted mb-0">Not provide or request Services for illegal products, prohibited content, or deceptive practices</p>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>

                                    <p className="terms-section-text mt-3">
                                        Failure to meet these obligations may result in delays or suspension of
                                        Services. Any delays caused by your failure to cooperate may affect
                                        project timelines and could result in additional fees.
                                    </p>
                                </section>

                                {/* Fees & Payment */}
                                <section id="fees-payment" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">5. Fees & Payment</h2>

                                    <h5 className="terms-subsection-title mb-3">5.1 Service Fees</h5>
                                    <p className="terms-section-text">
                                        Fees for our Services will be set out in each Statement of Work or as
                                        otherwise agreed in writing. Fees may include:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li><strong>Management Fees:</strong> Our fees for managing your campaigns and providing strategic guidance</li>
                                        <li><strong>Advertising Spend:</strong> Funds spent on Third-Party Platforms (billed at cost or with a markup as specified)</li>
                                        <li><strong>Setup Fees:</strong> One-time fees for initial campaign setup or platform configuration</li>
                                        <li><strong>Consultancy Fees:</strong> Hourly or project-based fees for strategic consulting</li>
                                    </ul>

                                    <h5 className="terms-subsection-title mb-3">5.2 Payment Terms</h5>
                                    <p className="terms-section-text">
                                        Unless otherwise specified in your SOW:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>Invoices are due within <strong>14 days</strong> of the invoice date</li>
                                        <li>Monthly recurring fees are invoiced at the beginning of each month</li>
                                        <li>Advertising spend is invoiced in advance or as prepaid funds</li>
                                        <li>Late payments may incur interest at <strong>8% per annum</strong> above the Bank of England base rate</li>
                                        <li>We reserve the right to suspend Services for unpaid invoices exceeding 30 days</li>
                                    </ul>

                                    <h5 className="terms-subsection-title mb-3">5.3 Advertising Spend</h5>
                                    <p className="terms-section-text">
                                        For Paid Media services, you are responsible for all advertising costs.
                                        We may require:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>Prepayment of estimated advertising spend</li>
                                        <li>A minimum monthly advertising budget as specified in your SOW</li>
                                        <li>Separate billing directly from advertising platforms (e.g., Google Ads)</li>
                                    </ul>
                                    <p className="terms-section-text">
                                        Any unused advertising prepayments will be rolled over or refunded upon
                                        termination, subject to platform policies and any non-refundable fees.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">5.4 Taxes</h5>
                                    <p className="terms-section-text">
                                        All fees are exclusive of VAT or other applicable taxes. Where we are
                                        required to charge VAT, it will be added at the prevailing rate.
                                        You agree to provide any tax information (e.g., VAT number) required for
                                        proper invoicing.
                                    </p>

                                    <div className="terms-alert-info mt-4">
                                        <strong>UK VAT Notice:</strong> Reachtix (QUANTUMCREST SOLUTIONS LTD)
                                        is registered for VAT in the United Kingdom. VAT will be charged on all
                                        Services provided to UK-based clients unless a valid VAT exemption applies.
                                    </div>
                                </section>

                                {/* Intellectual Property */}
                                <section id="intellectual-property" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">6. Intellectual Property</h2>

                                    <h5 className="terms-subsection-title mb-3">6.1 Our Intellectual Property</h5>
                                    <p className="terms-section-text">
                                        All Intellectual Property Rights in our pre-existing materials, methodologies,
                                        software, tools, templates, and know-how ("Reachtix IP") remain our sole
                                        property. Nothing in these Terms transfers ownership of Reachtix IP to you.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">6.2 Deliverables</h5>
                                    <p className="terms-section-text">
                                        Upon full payment of all fees due, we grant you a non-exclusive,
                                        perpetual, worldwide license to use the Deliverables for your internal
                                        business purposes. Unless otherwise specified in your SOW:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>You may not resell or redistribute Deliverables to third parties</li>
                                        <li>You may not remove our copyright or attribution notices</li>
                                        <li>Our underlying methodologies remain our property even when incorporated into Deliverables</li>
                                    </ul>

                                    <h5 className="terms-subsection-title mb-3">6.3 Your Intellectual Property</h5>
                                    <p className="terms-section-text">
                                        You retain ownership of all Intellectual Property Rights in your brand
                                        assets, content, and data. You grant us a limited license to use your IP
                                        solely for the purpose of providing the Services.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">6.4 Third-Party IP</h5>
                                    <p className="terms-section-text">
                                        Some Deliverables may incorporate third-party content or platforms.
                                        Your use of such content is subject to the applicable third-party
                                        terms and licenses.
                                    </p>
                                </section>

                                {/* Confidentiality */}
                                <section id="confidentiality" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">7. Confidentiality</h2>
                                    <p className="terms-section-text">
                                        Each party ("Disclosing Party") may disclose confidential information
                                        to the other ("Receiving Party") in connection with the Services.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">7.1 Definition of Confidential Information</h5>
                                    <p className="terms-section-text">
                                        "Confidential Information" includes all non-public information related
                                        to a party's business, including:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>Business strategies and financial information</li>
                                        <li>Customer data and lists</li>
                                        <li>Campaign performance data and proprietary methodologies</li>
                                        <li>Unpublished reports and analyses</li>
                                        <li>Login credentials and access information</li>
                                    </ul>

                                    <h5 className="terms-subsection-title mb-3">7.2 Obligations</h5>
                                    <p className="terms-section-text">
                                        The Receiving Party agrees to:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>Use Confidential Information only to perform obligations under these Terms</li>
                                        <li>Protect Confidential Information using reasonable security measures</li>
                                        <li>Not disclose Confidential Information to third parties without prior written consent</li>
                                        <li>Return or destroy Confidential Information upon request or termination</li>
                                    </ul>

                                    <p className="terms-section-text">
                                        These confidentiality obligations survive termination of these Terms
                                        for a period of <strong>five (5) years</strong>.
                                    </p>
                                </section>

                                {/* Limitation of Liability */}
                                <section id="liability" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">8. Limitation of Liability</h2>

                                    <div className="terms-alert-warning mt-2 mb-4">
                                        <strong>Please read this section carefully as it limits our liability to you.</strong>
                                    </div>

                                    <h5 className="terms-subsection-title mb-3">8.1 No Guarantee of Results</h5>
                                    <p className="terms-section-text">
                                        Digital marketing involves inherent uncertainty. We do not guarantee
                                        specific results, including but not limited to:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>Specific search engine rankings or positions</li>
                                        <li>Specific traffic volumes or conversion rates</li>
                                        <li>Specific return on ad spend (ROAS) or other performance metrics</li>
                                        <li>Ad approvals or account standing on Third-Party Platforms</li>
                                    </ul>
                                    <p className="terms-section-text">
                                        Any forecasts, estimates, or projections provided are for planning
                                        purposes only and do not constitute guarantees.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">8.2 Exclusion of Indirect Damages</h5>
                                    <p className="terms-section-text">
                                        To the maximum extent permitted by law, neither party shall be liable
                                        for any indirect, incidental, special, consequential, or punitive damages,
                                        including lost profits, lost revenue, lost data, or business interruption,
                                        even if advised of the possibility of such damages.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">8.3 Cap on Liability</h5>
                                    <p className="terms-section-text">
                                        Our total aggregate liability arising out of or relating to these Terms
                                        and the Services shall not exceed the total fees paid or payable by you
                                        to us in the <strong>twelve (12) months</strong> immediately preceding
                                        the event giving rise to the claim.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">8.4 UK Consumer Rights</h5>
                                    <p className="terms-section-text">
                                        Nothing in these Terms excludes or limits our liability for:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>Death or personal injury caused by our negligence</li>
                                        <li>Fraud or fraudulent misrepresentation</li>
                                        <li>Any liability that cannot be excluded or limited under applicable law</li>
                                    </ul>
                                </section>

                                {/* Warranties */}
                                <section id="warranties" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">9. Warranties & Disclaimers</h2>

                                    <h5 className="terms-subsection-title mb-3">9.1 Our Warranties</h5>
                                    <p className="terms-section-text">
                                        We warrant that:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>The Services will be performed in a professional and workmanlike manner</li>
                                        <li>We have the necessary skills and expertise to provide the Services</li>
                                        <li>The Services will comply with applicable UK laws and regulations</li>
                                    </ul>

                                    <h5 className="terms-subsection-title mb-3">9.2 Your Warranties</h5>
                                    <p className="terms-section-text">
                                        You warrant that:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>You have the authority to enter into these Terms</li>
                                        <li>All information you provide is accurate and complete</li>
                                        <li>Your products, services, and marketing materials comply with applicable laws</li>
                                        <li>You will not use our Services for any illegal or prohibited purpose</li>
                                    </ul>

                                    <h5 className="terms-subsection-title mb-3">9.3 Disclaimer of Warranties</h5>
                                    <p className="terms-section-text">
                                        EXCEPT AS EXPRESSLY PROVIDED IN THESE TERMS, AND TO THE MAXIMUM EXTENT
                                        PERMITTED BY APPLICABLE LAW, THE SERVICES ARE PROVIDED "AS IS" AND "AS
                                        AVAILABLE." WE DISCLAIM ALL OTHER WARRANTIES, EXPRESS OR IMPLIED,
                                        INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                                        PURPOSE, AND NON-INFRINGEMENT.
                                    </p>
                                    <p className="terms-section-text">
                                        We do not warrant that the Services will be uninterrupted, error-free,
                                        or completely secure, or that any defects will be corrected.
                                    </p>
                                </section>

                                {/* Termination */}
                                <section id="termination" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">10. Termination</h2>

                                    <h5 className="terms-subsection-title mb-3">10.1 Termination for Convenience</h5>
                                    <p className="terms-section-text">
                                        Either party may terminate these Terms or any SOW upon <strong>30 days'</strong>{' '}
                                        written notice to the other party. For month-to-month Services, termination
                                        may be effective at the end of any calendar month with 30 days' notice.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">10.2 Termination for Cause</h5>
                                    <p className="terms-section-text">
                                        Either party may terminate these Terms immediately upon written notice if:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>The other party commits a material breach and fails to remedy within 14 days of notice</li>
                                        <li>The other party becomes insolvent, enters liquidation, or ceases to trade</li>
                                        <li>The other party engages in fraudulent or illegal conduct</li>
                                    </ul>

                                    <h5 className="terms-subsection-title mb-3">10.3 Effect of Termination</h5>
                                    <p className="terms-section-text">
                                        Upon termination:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>You remain liable for all fees incurred prior to termination</li>
                                        <li>We will deliver completed Deliverables for which you have paid</li>
                                        <li>We will return or destroy your Confidential Information as instructed</li>
                                        <li>You will revoke our access to your accounts on Third-Party Platforms</li>
                                        <li>Unused advertising prepayments will be refunded within 30 days</li>
                                    </ul>
                                    <p className="terms-section-text">
                                        Sections regarding confidentiality, intellectual property, limitation
                                        of liability, and governing law survive termination.
                                    </p>
                                </section>

                                {/* Data Protection & GDPR */}
                                <section id="data-protection" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">11. Data Protection & GDPR</h2>
                                    <p className="terms-section-text">
                                        We are committed to protecting personal data in accordance with the
                                        UK General Data Protection Regulation (UK GDPR) and the Data Protection
                                        Act 2018.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">11.1 Data Processing</h5>
                                    <p className="terms-section-text">
                                        Where we process personal data on your behalf as a data processor,
                                        the terms of our <Link to="/data-processing" className="terms-inline-link">Data Processing Agreement (DPA)</Link>{' '}
                                        will apply. The DPA is incorporated into these Terms by reference.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">11.2 Your Responsibilities</h5>
                                    <p className="terms-section-text">
                                        You are responsible for:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>Having appropriate lawful bases for processing personal data</li>
                                        <li>Providing adequate privacy notices to data subjects</li>
                                        <li>Responding to data subject rights requests</li>
                                        <li>Ensuring that any data you provide was lawfully obtained</li>
                                    </ul>

                                    <h5 className="terms-subsection-title mb-3">11.3 Data Security</h5>
                                    <p className="terms-section-text">
                                        We implement appropriate technical and organizational security measures
                                        to protect personal data. In the event of a personal data breach,
                                        we will notify you without undue delay and as required by law.
                                    </p>

                                    <div className="terms-alert-info mt-4">
                                        For more information about how we handle personal data, please review our{' '}
                                        <Link to="/privacy-policy" className="terms-inline-link">Privacy Policy</Link>.
                                    </div>
                                </section>

                                {/* Force Majeure */}
                                <section id="force-majeure" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">12. Force Majeure</h2>
                                    <p className="terms-section-text">
                                        Neither party shall be liable for delays or failure to perform its
                                        obligations under these Terms (excluding payment obligations) where
                                        such delay or failure is caused by events beyond its reasonable control,
                                        including but not limited to:
                                    </p>
                                    <ul className="terms-section-list">
                                        <li>Acts of God, natural disasters, or extreme weather</li>
                                        <li>War, terrorism, civil unrest, or cyber attacks</li>
                                        <li>Pandemic, epidemic, or public health emergencies</li>
                                        <li>Government actions, sanctions, or changes in law</li>
                                        <li>Third-Party Platform outages or policy changes</li>
                                        <li>Internet infrastructure failures</li>
                                    </ul>
                                    <p className="terms-section-text">
                                        The affected party will notify the other as soon as reasonably practicable
                                        and will use reasonable efforts to mitigate the effects of the force majeure event.
                                    </p>
                                </section>

                                {/* Governing Law */}
                                <section id="governing-law" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">13. Governing Law & Jurisdiction</h2>
                                    <p className="terms-section-text">
                                        These Terms and any dispute or claim arising out of or in connection with
                                        them (including non-contractual disputes) shall be governed by and
                                        construed in accordance with the laws of <strong>England and Wales</strong>.
                                    </p>
                                    <p className="terms-section-text">
                                        The parties irrevocably agree that the courts of <strong>London, England</strong>{' '}
                                        shall have exclusive jurisdiction to settle any dispute or claim arising
                                        out of or in connection with these Terms.
                                    </p>
                                    <div className="terms-company-info mt-4 p-3 bg-light rounded">
                                        <p className="mb-1"><strong>Company Information:</strong></p>
                                        <p className="mb-1 small">QUANTUMCREST SOLUTIONS LTD (trading as Reachtix)</p>
                                        <p className="mb-1 small">Company Number: 16088299</p>
                                        <p className="mb-0 small">Registered Office: 330 High Holborn, London, WC1V 7QH, United Kingdom</p>
                                    </div>
                                </section>

                                {/* General Provisions */}
                                <section id="general" className="terms-content-section mb-5">
                                    <h2 className="terms-section-title fw-bold mb-4">14. General Provisions</h2>

                                    <h5 className="terms-subsection-title mb-3">14.1 Entire Agreement</h5>
                                    <p className="terms-section-text">
                                        These Terms, together with any SOWs and the DPA, constitute the entire
                                        agreement between the parties and supersede all prior agreements,
                                        understandings, or representations.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">14.2 Amendments</h5>
                                    <p className="terms-section-text">
                                        We may update these Terms from time to time. Material changes will be
                                        notified to you by email or through our website. Continued use of our
                                        Services after notice constitutes acceptance of the updated Terms.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">14.3 Waiver and Severability</h5>
                                    <p className="terms-section-text">
                                        Failure to enforce any provision of these Terms does not constitute a
                                        waiver. If any provision is found to be unenforceable, the remaining
                                        provisions remain in full force and effect.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">14.4 Assignment</h5>
                                    <p className="terms-section-text">
                                        You may not assign or transfer these Terms without our prior written
                                        consent. We may assign these Terms to an affiliate or in connection
                                        with a merger or acquisition.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">14.5 Third-Party Rights</h5>
                                    <p className="terms-section-text">
                                        These Terms do not confer any rights or remedies on any third party,
                                        except as expressly stated.
                                    </p>

                                    <h5 className="terms-subsection-title mb-3">14.6 Electronic Communications</h5>
                                    <p className="terms-section-text">
                                        You agree to receive electronic communications from us (e.g., via email)
                                        and that electronic notices satisfy any legal requirement for written
                                        communication.
                                    </p>
                                </section>



                                {/* Acknowledgement Section */}
                                <div className="terms-acknowledgement-card card bg-light border-0 mt-4">
                                    <div className="card-body text-center">
                                        <i className="fas fa-check-circle fa-2x text-primary mb-3"></i>
                                        <h6 className="fw-bold mb-2">Acknowledgement of Terms</h6>
                                        <p className="small mb-0">
                                            By engaging our Services, you acknowledge that you have read,
                                            understood, and agree to be bound by these Terms and Conditions.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Quick Links Section */}
            <section className="terms-quicklinks-section py-5 bg-light">
                <Container>
                    <Row className="text-center">
                        <Col>
                            <h3 className="terms-quicklinks-title fw-bold mb-4">Related Legal Documents</h3>
                            <div className="terms-quicklinks-container d-flex justify-content-center flex-wrap gap-3">
                                <Link to="/privacy-policy" className="terms-quicklink-btn btn btn-outline-primary">
                                    Privacy Policy
                                </Link>
                                <Link to="/cookie-policy" className="terms-quicklink-btn btn btn-outline-primary">
                                    Cookie Policy
                                </Link>
                                <Link to="/data-processing" className="terms-quicklink-btn btn btn-outline-primary">
                                    Data Processing Agreement
                                </Link>
                                <Link to="/contact" className="terms-quicklink-btn btn btn-outline-primary">
                                    Contact Us
                                </Link>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default TermsPage;